import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import MinecraftText from '../components/MinecraftText';
import Modal from '../components/Modal';
import SellbotNav from '../components/SellbotNav';
import SellbotReasonItems from '../components/SellbotReasonItems';
import { Pagination } from '../components/Pagination';
import SellbotZoneFilter from '../components/SellbotZoneFilter';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { useSellbotReason } from '../hooks/useSellbotReason';
import { useSellbotZones, ZONE_FILTER_ALL } from '../hooks/useSellbotZones';
import { formatDateTime } from '../services/dateFormat';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import type { SellbotActivityDto } from '../api';
import styles from './Sellbot.module.css';

const LOG_LEVELS = ['Debug', 'Info', 'Warning', 'Error'] as const;

function levelClass(level: string, styles: Record<string, string>): string {
    switch (level) {
        case 'Error': return styles.badgeDanger;
        case 'Warning': return styles.badgeWarning;
        case 'Info': return styles.badgeInfo;
        default: return styles.badgeMuted;
    }
}

export default function SellbotActivity() {
    const api = useApi();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { reasonText, reasonItems } = useSellbotReason();
    const { botId } = useActiveBot();
    const { multiZone, zones, zoneLabel, matchesZoneFilter } = useSellbotZones(botId);

    const [minLevel, setMinLevel] = createSignal<typeof LOG_LEVELS[number]>('Info');
    const [search, setSearch] = createSignal('');
    const [zoneFilter, setZoneFilter] = createSignal(ZONE_FILTER_ALL);
    const [page, setPage] = createSignal(0);
    const pageSize = 30;

    const [activityData, { refetch: refetchActivity }] = createResource(
        () => ({ id: botId(), minLevel: minLevel() }),
        async (params) => {
            if (!params.id) return [] as SellbotActivityDto[];
            const res = await api.sellbot.getActivity(params.id, { take: 1000, minLevel: params.minLevel });
            return isOk(res.data) ? res.data.value.entries : [];
        }
    );

    createEffect(() => {
        const id = botId();
        if (!id) return;
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_event' && evt.payload?.source === 'sellbot') void refetchActivity();
        });
        onCleanup(unsub);
    });

    const filteredEntries = createMemo(() => {
        const q = search().trim().toLowerCase();
        const selectedZone = zoneFilter();
        const all = (activityData() ?? []).filter(a => matchesZoneFilter(a.sellbotZoneId, selectedZone));
        if (!q) return all;
        return all.filter(a => (a.playerName ?? '').toLowerCase().includes(q) || t('SELLBOT_ACT_' + a.type).toLowerCase().includes(q));
    });

    const totalPages = createMemo(() => Math.max(1, Math.ceil(filteredEntries().length / pageSize)));
    const pageEntries = createMemo(() => filteredEntries().slice(page() * pageSize, page() * pageSize + pageSize));

    const setMinLevelAndReset = (v: typeof LOG_LEVELS[number]) => { setMinLevel(v); setPage(0); };

    const [detailAct, setDetailAct] = createSignal<SellbotActivityDto | null>(null);
    const closeDetail = () => setDetailAct(null);
    const openTransactionDetail = (id: string) => { setDetailAct(null); navigate(`/sellbot/transactions/${id}`); };

    return (
        <div class={`${styles.container} ${styles.feedPage}`}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_ACTIVITY_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_ACTIVITY_SUBTITLE" /></span>
                </div>
            </div>

            <div class={`fcard v ${styles.feedCard}`} style={{ gap: 'var(--spacing-md)' }}>
                <div class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                    <input type="text" placeholder={t('SELLBOT_ACTIVITY_SEARCH_PLACEHOLDER')} value={search()} onInput={(e) => { setSearch(e.currentTarget.value); setPage(0); }} style={{ flex: '1', 'min-width': '12rem' }} />
                    <select value={minLevel()} onChange={(e) => setMinLevelAndReset(e.currentTarget.value as typeof LOG_LEVELS[number])}>
                        <For each={LOG_LEVELS}>{(l) => <option value={l}>{t('SELLBOT_LOG_' + l.toUpperCase())}</option>}</For>
                    </select>
                    <Show when={multiZone()}>
                        <SellbotZoneFilter zones={zones()} value={zoneFilter()} onChange={(value) => { setZoneFilter(value); setPage(0); }} />
                    </Show>
                </div>

                <Show when={activityData.state !== 'pending'} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                    <Show when={pageEntries().length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-stream" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_NO_ACTIVITY" /></p></div>}>
                        <div class={styles.feedList}>
                            <For each={pageEntries()}>{(a) => (
                                <div class={`${styles.actRow} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => setDetailAct(a)}>
                                    <span class={`${styles.badge} ${levelClass(a.level, styles)}`}>{t('SELLBOT_LOG_' + a.level.toUpperCase())}</span>
                                    <span class={styles.actType}>
                                        {t('SELLBOT_ACT_' + a.type)}<Show when={a.playerName}> · <MinecraftText text={a.playerName!} /></Show>
                                        <Show when={zoneLabel(a.sellbotZoneId)}>{(label) => <span class={styles.txnItem}> · <i class="fa-solid fa-location-dot" /> {label()}</span>}</Show>
                                    </span>
                                    <span class={styles.txnTime}>{formatDateTime(a.createdAt)}</span>
                                    <Show when={reasonItems(a.reasonArgsJson)}>
                                        {(items) => <div class={styles.actItems}><SellbotReasonItems items={items()} showNames={false} /></div>}
                                    </Show>
                                </div>
                            )}</For>
                        </div>
                        <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
                    </Show>
                </Show>
            </div>

            <Modal open={!!detailAct()} onClose={closeDetail} title={<Text val="SELLBOT_DETAIL_ACT_TITLE" />} subtitle={<Text val="SELLBOT_DETAIL_SUBTITLE" />}>
                <Show when={detailAct()}>{(a) => (
                    <div class={styles.detailGrid}>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_LEVEL" /></span>
                        <span><span class={`${styles.badge} ${levelClass(a().level, styles)}`}>{t('SELLBOT_LOG_' + a().level.toUpperCase())}</span></span>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_TYPE" /></span>
                        <span>{t('SELLBOT_ACT_' + a().type)}</span>
                        <Show when={a().reasonCode || a().message}>
                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_MESSAGE" /></span>
                            <span class={styles.detailMessage}>{reasonText(a().reasonCode, a().reasonArgsJson, a().message)}</span>
                        </Show>
                        <Show when={reasonItems(a().reasonArgsJson)}>
                            {(items) => (
                                <>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_HELD_ITEMS" /></span>
                                    <SellbotReasonItems items={items()} />
                                </>
                            )}
                        </Show>
                        <Show when={a().playerName}>
                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_PLAYER" /></span>
                            <span><MinecraftText text={a().playerName!} /></span>
                        </Show>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_CREATED" /></span>
                        <span>{formatDateTime(a().createdAt)}</span>
                        <Show when={a().transactionId}>
                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_LINKED_TXN" /></span>
                            <span class={styles.clickable} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => openTransactionDetail(a().transactionId!)}>
                                <i class="fa-solid fa-arrow-up-right-from-square" /> <Text val="SELLBOT_DETAIL_OPEN" />
                            </span>
                        </Show>
                    </div>
                )}</Show>
            </Modal>
        </div>
    );
}
